// src/components/Layout.js

import { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Layout.css";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { FaBars, FaGlobe, FaMoon, FaSun } from "react-icons/fa";
import { HiUserCircle } from "react-icons/hi2";
import { UserContext } from "../context/UserContext";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import logo from "../assets/logo.png";

const Layout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { user, isloading, logout } = useContext(UserContext);
  const { lang, setLang, t } = useLanguage();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const sidebarRef = useRef(null);

  // Obtenir le nom de la page actuelle
  const currentPageName = useMemo(() => {
    const path = location.pathname.toLowerCase();
    
    // Normaliser les clés du dictionnaire en minuscules pour la comparaison
    const normalizedPages = Object.keys(t.pages).reduce((acc, key) => {
      acc[key.toLowerCase()] = t.pages[key];
      return acc;
    }, {});
    
    // Vérifier les routes exactes d'abord
    if (normalizedPages[path]) {
      return normalizedPages[path];
    }
    
    // Vérifier les routes avec paramètres (ex: /animaux/:id/modifier)
    if (path.includes("/animaux/")) {
      if (path.includes("/modifier")) return lang === "fr" ? "Modifier Animal" : "Edit Animal";
      if (path.includes("/fiche")) return lang === "fr" ? "Fiche Animal" : "Animal Details";
      return normalizedPages["/animaux"] || t.pages["/animaux"] || "Animaux";
    }
    if (path.includes("/troupeaux/")) {
      if (path.includes("/modifier")) return lang === "fr" ? "Modifier Troupeau" : "Edit Herd";
      if (path.includes("/rapport")) return lang === "fr" ? "Rapport Troupeau" : "Herd Report";
      return normalizedPages["/troupeaux"] || t.pages["/troupeaux"] || "Troupeaux";
    }
    if (path.includes("/ranches/")) {
      if (path.includes("/modifier")) return lang === "fr" ? "Modifier Ranch" : "Edit Ranch";
      return normalizedPages["/ranches"] || t.pages["/ranches"] || "Ranch de Provenance";
    }
    if (path.includes("/sante/")) {
      if (path.includes("/modifier")) return lang === "fr" ? "Modifier Prophylaxie" : "Edit Prophylaxis";
      return normalizedPages["/sante"] || t.pages["/sante"] || (lang === "fr" ? "Prophylaxie Et Traitement Curatif" : "Prophylaxis And Curative Treatment");
    }
    if (path.includes("/evenements/")) {
      if (path.includes("/modifier")) return lang === "fr" ? "Modifier Evenement" : "Edit Event";
      return normalizedPages["/evenements"] || t.pages["/evenements"] || "Evenements";
    }
    if (path.includes("/reproductions/")) {
      if (path.includes("/modifier")) return lang === "fr" ? "Modifier Reproduction" : "Edit Reproduction";
      return normalizedPages["/reproductions"] || t.pages["/reproductions"] || "Reproductions";
    }
    if (path.includes("/users/")) {
      if (path.includes("/modifier")) return lang === "fr" ? "Modifier Utilisateur" : "Edit User";
      return lang === "fr" ? "Utilisateurs" : "Users";
    }
    
    // Par défaut, retourner le nom de l'application
    return t.appName;
  }, [location.pathname, t, lang]);

  const displayName = useMemo(() => {
    if (isloading) return t.loading;
    if (!user) return t.guest;
    return (
      user.username ||
      user.nom ||
      user.name ||
      user.first_name ||
      user.email ||
      t.user
    );
  }, [user, isloading, t]);

  // Détecter la taille de l'écran et fermer la sidebar automatiquement sur mobile
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Fermer automatiquement la sidebar sur mobile
      if (mobile) {
        setIsSidebarOpen(false);
      } else {
        // Ouvrir automatiquement sur desktop
        setIsSidebarOpen(true);
      }
    };

    // Vérifier au chargement
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fermer la sidebar au clic en dehors sur mobile
  useEffect(() => {
    if (!isMobile || !isSidebarOpen) return;

    const handleClickOutside = (e) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target) &&
        !e.target.closest(".topbar-toggle")
      ) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, isSidebarOpen]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!isUserMenuOpen) return;
      if (!userMenuRef.current) return;
      if (!userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsUserMenuOpen(false);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isUserMenuOpen]);

  const handleLogout = () => {
    // Clear all known token keys to match the current codebase (token vs access_token/refresh_token)
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    try {
      logout?.();
    } catch (e) {
      // ignore
    }
    setIsUserMenuOpen(false);
    navigate("/");
  };

  return (
    <div className="layout-container">
      {isMobile && isSidebarOpen && (
        <div 
          className="sidebar-backdrop" 
          onClick={() => setIsSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside 
        ref={sidebarRef}
        className={`sidebar ${isSidebarOpen ? "" : "closed"} ${isMobile ? "mobile" : ""}`}
      >
        <div className="sidebar-top">
          <div className="brand">
            <div className="brand-mark">
              <img src={logo} alt="Ranch de Ngaoundaba" className="brand-logo" />
            </div>
            {isSidebarOpen && (
              <div className="brand-text">
                <span className="brand-name">KY NGAOUNDABA</span>
                <span className="brand-subtitle">{t.brandSubtitle}</span>
              </div>
            )}
          </div>
        </div>

        <nav className="nav">
          {/* Vue d'ensemble */}
          <NavLink to="/dashboard" className="nav-link" title={t.navDashboard}>
            <span className="nav-icon">📊</span>
            {isSidebarOpen && <span>{t.navDashboard}</span>}
          </NavLink>

          {/* Gestion du cheptel */}
          {hasPermission(user, PERMISSIONS.VIEW_ANIMAUX) && (
            <NavLink to="/animaux" className="nav-link" title={t.navAnimals}>
              <span className="nav-icon">🐄</span>
              {isSidebarOpen && <span>{t.navAnimals}</span>}
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.VIEW_TROUPEAUX) && (
            <NavLink to="/troupeaux" className="nav-link" title={t.navHerds}>
              <span className="nav-icon">👥</span>
              {isSidebarOpen && <span>{t.navHerds}</span>}
            </NavLink>
          )}
          {/* Temporairement caché - Ranch de Provenance */}
          {/* <NavLink to="/ranches" className="nav-link" title="Ranch de Provenance">
            <span className="nav-icon">🏢</span>
            {isSidebarOpen && <span>Ranch de Provenance</span>}
          </NavLink> */}

          {/* Santé et suivi */}
          {hasPermission(user, PERMISSIONS.VIEW_SANTE) && (
            <NavLink to="/sante" className="nav-link" title={t.navProphylaxis}>
              <span className="nav-icon">💉</span>
              {isSidebarOpen && <span>{t.navProphylaxis}</span>}
            </NavLink>
          )}
          {/* Temporairement caché - Reproductions */}
          {/* <NavLink to="/reproductions" className="nav-link" title="Reproductions">
            <span className="nav-icon">🔬</span>
            {isSidebarOpen && <span>Reproductions</span>}
          </NavLink> */}

          {/* Pharmacie */}
          {hasPermission(user, PERMISSIONS.VIEW_PHARMACIE) && (
            <NavLink to="/dashboard-pharmacie" className="nav-link" title={t.navPharmacy}>
              <span className="nav-icon">💊</span>
              {isSidebarOpen && <span>{t.navPharmacy}</span>}
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.VIEW_EVENEMENT) && (
            <NavLink to="/evenements" className="nav-link" title={t.navEvents}>
              <span className="nav-icon">📅</span>
              {isSidebarOpen && <span>{t.navEvents}</span>}
            </NavLink>
          )}

          {/* Rapports et alertes */}
          {hasPermission(user, PERMISSIONS.VIEW_RAPPORT) && (
            <NavLink to="/rapports" className="nav-link" title={t.navReports}>
              <span className="nav-icon">📈</span>
              {isSidebarOpen && <span>{t.navReports}</span>}
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.VIEW_ALERTE) && (
            <NavLink to="/alertes" className="nav-link" title={t.navAlerts}>
              <span className="nav-icon">🔔</span>
              {isSidebarOpen && <span>{t.navAlerts}</span>}
            </NavLink>
          )}

          {/* Administration */}
          {user?.is_admin && (
            <NavLink to="/users" className="nav-link" title={t.navUsers}>
              <span className="nav-icon">👤</span>
              {isSidebarOpen && <span>{t.navUsers}</span>}
            </NavLink>
          )}
          {hasPermission(user, PERMISSIONS.VIEW_BERGER) && (
            <NavLink to="/enregistrer-berger" className="nav-link" title={t.navShepherds}>
              <span className="nav-icon">👨‍🌾</span>
              {isSidebarOpen && <span>{t.navShepherds}</span>}
            </NavLink>
          )}
          {/* Test - À supprimer après validation */}
          {/* Caché temporairement
          {user?.is_admin && (
            <NavLink to="/test-datagrid" className="nav-link" title="Test DataGrid">
              <span className="nav-icon">🧪</span>
              {isSidebarOpen && <span>Test DataGrid</span>}
            </NavLink>
          )}
          */}
        </nav>

        <div className="sidebar-footer">
          <NavLink to="/logout" className="nav-link logout-link" title={t.navDisconnect}>
            <span className="nav-icon">🚪</span>
            {isSidebarOpen && <span>{t.navDisconnect}</span>}
          </NavLink>
        </div>
      </aside>

      <div className={`main-content ${isSidebarOpen ? "" : "expanded"}`}>
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="topbar-toggle"
              type="button"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              aria-label={isSidebarOpen ? t.closeMenu : t.openMenu}
            >
              <FaBars />
            </button>
            <div className="topbar-brand">
              <img src={logo} alt="Ranch de Ngaoundaba" className="topbar-logo" />
              <span className="topbar-brand-title">{currentPageName}</span>
            </div>
          </div>

          <div className="topbar-right">
            <div className="lang-select" aria-label={t.language} title={t.language}>
              <FaGlobe className="lang-icon" aria-hidden="true" />
              <select value={lang} onChange={(e) => setLang(e.target.value)}>
                <option value="fr">FR</option>
                <option value="en">EN</option>
              </select>
            </div>

            <button
              className="theme-toggle"
              type="button"
              onClick={toggleTheme}
              aria-label={isDark ? t.lightMode : t.darkMode}
              title={isDark ? t.lightMode : t.darkMode}
            >
              {isDark ? <FaSun /> : <FaMoon />}
            </button>

            <div className="user-menu" ref={userMenuRef}>
              <button
                type="button"
                className="user-pill user-pill-button"
                onClick={() => setIsUserMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen}
                title={displayName}
              >
                <span className="user-avatar" aria-hidden="true">
                  <HiUserCircle className="user-avatar-icon" aria-hidden="true" />
                </span>
                <span className="user-name">{displayName}</span>
                <span className="user-caret" aria-hidden="true" />
              </button>

              {isUserMenuOpen && (
                <div className="user-dropdown" role="menu">
                  <button 
                    type="button" 
                    className="user-dropdown-item" 
                    role="menuitem" 
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      navigate("/parametres");
                    }}
                  >
                    {t.settings}
                  </button>
                  <button type="button" className="user-dropdown-item" role="menuitem" onClick={handleLogout}>
                    {t.disconnect}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="content">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
